import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Branding assets
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function TableNo30() {
  const pdfRef = useRef();

  // 📊 Gender Share (2024)
  const data = [
    { ageGroup: "14–18", femaleShare: 1.96, maleShare: 0.99 },
    { ageGroup: "19–21", femaleShare: 4.16, maleShare: 3.49 },
    { ageGroup: "22–24", femaleShare: 6.1, maleShare: 5.57 },
    { ageGroup: "25–34", femaleShare: 21.73, maleShare: 23.32 },
    { ageGroup: "35–44", femaleShare: 21.93, maleShare: 23.57 },
    { ageGroup: "45–54", femaleShare: 21.77, maleShare: 20.83 },
    { ageGroup: "55–64", femaleShare: 16.01, maleShare: 16.11 },
    { ageGroup: "65–99", femaleShare: 6.35, maleShare: 6.11 },
  ];

  // 🧩 Insights
  const topFemale = data.reduce((max, d) =>
    d.femaleShare > max.femaleShare ? d : max
  );
  const topMale = data.reduce((max, d) =>
    d.maleShare > max.maleShare ? d : max
  );
  const avgGap = (
    data.reduce((s, d) => s + Math.abs(d.femaleShare - d.maleShare), 0) /
    data.length
  ).toFixed(2);

  // 📈 Chart Data
  const chartData = {
    labels: data.map((d) => d.ageGroup),
    datasets: [
      {
        label: "Female Share (%)",
        data: data.map((d) => d.femaleShare),
        backgroundColor: "rgba(236, 72, 153, 0.8)", // rose tone
        borderRadius: 6,
      },
      {
        label: "Male Share (%)",
        data: data.map((d) => d.maleShare),
        backgroundColor: "rgba(14, 165, 233, 0.8)", // blue tone
        borderRadius: 6,
      },
    ],
  };

  // ⚙️ Chart Options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { color: "#111827", font: { size: 13, weight: "bold" } },
      },
      title: {
        display: true,
        text: "📊 Gender Share by Age Group (2024)",
        color: "#111827",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => v + "%", color: "#4B5563" },
        title: {
          display: true,
          text: "Percentage Share (%)",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        ticks: { color: "#4B5563" },
        title: {
          display: true,
          text: "Age Groups",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // 🧾 PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("gender-share-by-age-group.pdf");
      // input.style.width = originalWidth;
    });
  };

  // 💅 UI
  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-rose-50 rounded-xl shadow-lg"
      >
        {/* Header */}
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Gender Share Analysis (2024)
        </h2>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-rose-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest Female Share</p>
            <p className="text-xl font-bold text-rose-700">
              {topFemale.ageGroup} ({topFemale.femaleShare.toFixed(2)}%)
            </p>
          </div>
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest Male Share</p>
            <p className="text-xl font-bold text-blue-700">
              {topMale.ageGroup} ({topMale.maleShare.toFixed(2)}%)
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Average Gender Gap</p>
            <p className="text-xl font-bold text-green-700">{avgGap}%</p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto rounded-lg shadow-md">
            <table className="w-full border-collapse text-center">
              <thead className="bg-blue-700 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">♀ Female (%)</th>
                  <th className="p-2">♂ Male (%)</th>
                  <th className="p-2">Gap (♀−♂)</th>
                </tr>
              </thead>
              <tbody>
                {data.map((d, i) => {
                  const gap = (d.femaleShare - d.maleShare).toFixed(2);
                  const rowColor =
                    d.ageGroup === topFemale.ageGroup
                      ? "bg-rose-50"
                      : d.ageGroup === topMale.ageGroup
                        ? "bg-blue-50"
                        : "bg-white";
                  return (
                    <tr key={i} className={`hover:bg-gray-100 ${rowColor}`}>
                      <td className="p-2 font-semibold">{d.ageGroup}</td>
                      <td className="p-2 text-rose-700">
                        {d.femaleShare.toFixed(2)}%
                      </td>
                      <td className="p-2 text-blue-700">
                        {d.maleShare.toFixed(2)}%
                      </td>
                      <td
                        className={`p-2 font-semibold ${
                          gap > 0 ? "text-rose-700" : "text-blue-700"
                        }`}
                      >
                        {gap > 0 ? "+" : ""}
                        {gap}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="text-center mt-4 text-gray-700">
          <p className="text-sm">
            📊 The{" "}
            <span className="font-semibold text-rose-700">
              {topFemale.ageGroup}
            </span>{" "}
            age group has the highest female share (
            {topFemale.femaleShare.toFixed(2)}%), while{" "}
            <span className="font-semibold text-blue-700">
              {topMale.ageGroup}
            </span>{" "}
            leads in male share ({topMale.maleShare.toFixed(2)}%). Average
            gender gap across all groups is{" "}
            <span className="font-semibold text-green-700">{avgGap}%</span>.
          </p>
        </div>
      </div>

      {/* PDF Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
